import React, { useState } from "react";
import { Row, Collapse, Icon } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { updatePreRegisterDetails } from "../../../../redux/slices/PreregisterDetailsSlice";
import { userProfiles } from "../../../../redux/slices/UserProfilesSlice";
import { participantsDetails } from "../../../../redux/slices/ParticipantsDetailsSlice";
import { checkSpecialRole } from "../../../../utils/userRolesUtils";
import { checkSpecialDestination } from "../../../../utils/postalCodeUtils";
import useImei from "../../../../hooks/useImei";
import useDataLogger from "../../../../hooks/useDataLogger";
import StandardShipmentForm from "./ShipmentForms/StandardShipmentForm.jsx";
import ProximityShipmentForm from "./ShipmentForms/ProximityShipmentForm.jsx";
import ColdchainShipmentForm from "./ShipmentForms/ColdchainShipmentForm.jsx";

const { Panel } = Collapse;

export default function ShipmentBlock({
  form,
  serviceType,
  productType,
  postalCodeRestrictions,
  validatedPostalCodes,
  searchOptions,
}) {
  const dispatch = useDispatch();
  const reduxUserRoles = useSelector(userProfiles);
  const reduxParticipants = useSelector(participantsDetails);
  const hasSpecialRole = checkSpecialRole(reduxUserRoles?.data);
  const hasRecipientSpecialPostCode = checkSpecialDestination(
    reduxParticipants?.recipientPostalCode,
  );
  const { getFieldDecorator, setFields, setFieldsValue, getFieldValue } = form;

  const { getImeiInfo, imeiDetails } = useImei();
  const { checkValidDataLogger } = useDataLogger();

  const [shipmentDetails, setShipmentDetails] = useState({
    declaredValue: false,
    paymentOnDelivery: false,
    mustShowDataLogger: false,
    mustFillDatalogger: false,
  });

  const proximityService = serviceType?.includes("Proximidad");

  const paqFarmaProduct = productType?.key?.includes("FRAXS");
  const pkColdchainProduct = productType?.key?.includes("PADXA");

  let sameDayDelivery = false;
  if (proximityService) {
    const match = postalCodeRestrictions?.find(
      (item) =>
        item.originPostalCode === validatedPostalCodes.sender &&
        item.destinationPostalCode === validatedPostalCodes.recipient,
    );
    sameDayDelivery = !!match?.sameDayDelivery;
  }

  const handleShipmentDetails = (name, value) => {
    if (value === false) {
      // borramos los errores del campo bankAccount si los hubiera dado que ya no es campo obligatorio

      setFields((prevState) => ({
        senderBankAccount: {
          value: prevState.senderBankAccount,
          errors: null,
        },
      }));
    }
    setShipmentDetails((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    if (name === "paymentOnDelivery") {
      dispatch(updatePreRegisterDetails({ paymentOnDelivery: value }));
    }
  };

  const handleImeiDetails = async (imeiValue) => {
    if (!imeiValue) {
      ["weight", "width", "length", "height"].forEach((field) => {
        setFieldsValue({
          [field]: undefined,
        });
      });
    }

    const response = await getImeiInfo(imeiValue);

    const temperatureTraceabilityIDValue = getFieldValue(
      "temperatureTraceabilityID",
    );

    if (response) {
      setFieldsValue({
        weight: response?.weight,
        length: response?.length,
        width: response?.width,
        height: response?.height,
        ...(temperatureTraceabilityIDValue?.key === 3
          ? { dataLoggerID: imeiValue }
          : {}),
      });
      return response;
    }
  };

  const handleImeiInputChange = (value, fieldName) => {
    if (fieldName !== "imei") return;

    form.resetFields(["temperatureTraceabilityID", "dataLoggerID"]);

    setShipmentDetails((prev) => ({
      ...prev,
      mustShowDataLogger: false,
      mustFillDatalogger: false,
    }));
  };

  const handleTrazability = (option, name) => {
    const { key } = option;
    //OFFLINE : sin datalogger temporalmente
    if (key == 2) {
      handleShipmentDetails("mustShowDataLogger", false);
      handleShipmentDetails("mustFillDatalogger", false);
      return;
    }
    //ONLINE
    if (key == 3) {
      handleShipmentDetails("mustShowDataLogger", true);
      handleShipmentDetails("mustFillDatalogger", true);
      return;
    }

    handleShipmentDetails("mustShowDataLogger", false);
    handleShipmentDetails("mustFillDatalogger", false);
  };

  return (
    <Row className="form-section">
      <Collapse
        defaultActiveKey={["1"]}
        expandIcon={({ isActive }) => (
          <Icon type="caret-right" rotate={isActive ? 90 : 0} />
        )}
      >
        <Panel
          header="Envío"
          key="1"
          extra={<Icon type="minus-circle" />}
          className="Busca"
        >
          {proximityService && (
            <ProximityShipmentForm
              form={form}
              shipmentDetails={shipmentDetails}
              handleShipmentDetails={handleShipmentDetails}
              hasSpecialRole={hasSpecialRole}
              hasRecipientSpecialPostCode={hasRecipientSpecialPostCode}
              sameDayDelivery={sameDayDelivery}
            />
          )}
          {!proximityService && !pkColdchainProduct && (
            <StandardShipmentForm
              form={form}
              shipmentDetails={shipmentDetails}
              handleShipmentDetails={handleShipmentDetails}
              handleImeiDetails={handleImeiDetails}
              handleImeiInputChange={handleImeiInputChange}
              handleTrazability={handleTrazability}
              imeiDetails={imeiDetails}
              hasSpecialRole={hasSpecialRole}
              hasRecipientSpecialPostCode={hasRecipientSpecialPostCode}
              checkValidDataLogger={checkValidDataLogger}
              searchOptions={searchOptions}
              paqFarmaProduct={paqFarmaProduct}
            />
          )}
          {!proximityService && pkColdchainProduct && (
            <ColdchainShipmentForm
              form={form}
              shipmentDetails={shipmentDetails}
              handleShipmentDetails={handleShipmentDetails}
              handleImeiDetails={handleImeiDetails}
              handleImeiInputChange={handleImeiInputChange}
              imeiDetails={imeiDetails}
              hasSpecialRole={hasSpecialRole}
              hasRecipientSpecialPostCode={hasRecipientSpecialPostCode}
              searchOptions={searchOptions}
              checkValidDataLogger={checkValidDataLogger}
            />
          )}
        </Panel>
      </Collapse>
    </Row>
  );
}
